import { API_BASE_URL, SERVICEURL } from "../constants/Constants";
import axiosInterceptor from "../helpers/Interceptor";

// AI Draft Report endpoints (augmento-backend AiDraftController). Errors are returned as the
// response rather than redirecting: a 403 here means "not your study", not "logged out".
const aiDraftUrl = (studyId) => `${API_BASE_URL}${SERVICEURL}/study/${studyId}/ai-draft`;

export const aiDraftEventsUrl = (studyId) => `${aiDraftUrl(studyId)}/events`;

// Every generation of the study, oldest first: { enabled, keyImageCount, maxConcurrent, generations }
export const getAiDraftGenerations = async (studyId) => {
  try {
    return await axiosInterceptor.get(`${aiDraftUrl(studyId)}/generations`);
  } catch (error) {
    return error.response;
  }
};

// Starts a new generation. modelId: one of the offered models, or null for the user's default.
// templateId: report template loaded in the editor (sent so the draft follows it), or null.
// output: "report" (default), "findings" or "both"; findings need the LLM report enabled for the user.
export const requestAiDraft = async (studyId, modelId = null, templateId = null, output = null) => {
  const body = {};
  if (modelId) body.model_id = modelId;
  if (templateId) body.template_id = templateId;
  if (output && output !== "report") body.output = output;
  try {
    return await axiosInterceptor.post(aiDraftUrl(studyId), body);
  } catch (error) {
    return error.response;
  }
};

// Models the radiologist can pick, with the system and personal defaults; 403 = AI draft not in use
export const getAiDraftModels = async () => {
  try {
    return await axiosInterceptor.get(`${API_BASE_URL}${SERVICEURL}/ai-draft/models`);
  } catch (error) {
    return error.response;
  }
};

export const markAiDraftViewed = async (studyId, jobId) => {
  try {
    return await axiosInterceptor.post(`${aiDraftUrl(studyId)}/${jobId}/viewed`, {});
  } catch (error) {
    return error.response;
  }
};

// HTML of a report template (Froala and legacy variants) to tell whether the report is still just that template
export const getAiDraftTemplateHtml = async (studyId, templateId) => {
  try {
    return await axiosInterceptor.get(`${aiDraftUrl(studyId)}/template/${templateId}`);
  } catch (error) {
    return error.response;
  }
};

// The generation whose draft was inserted into the report (records selected_at)
export const selectAiDraft = async (studyId, jobId) => {
  try {
    return await axiosInterceptor.post(`${aiDraftUrl(studyId)}/${jobId}/select`, {});
  } catch (error) {
    return error.response;
  }
};

// ---- super-admin: per-site AI draft settings (AiDraftAdminController) ----

// A function, not a constant: read at call time, after Constants has initialised (import cycle).
const adminUrl = () => `${API_BASE_URL}${SERVICEURL}/admin/ai-report`;

export const getAiReportProfiles = async () => {
  try {
    return await axiosInterceptor.get(`${adminUrl()}/profiles`);
  } catch (error) {
    return error.response;
  }
};

export const getAiDraftSiteSettings = async (firmId) => {
  try {
    return await axiosInterceptor.get(`${adminUrl()}/sites/${firmId}`);
  } catch (error) {
    return error.response;
  }
};

export const saveAiDraftSiteSettings = async (firmId, { enabled, profileId }) => {
  try {
    return await axiosInterceptor.post(`${adminUrl()}/sites/${firmId}`, {
      enabled,
      profileId: profileId || null,
    });
  } catch (error) {
    return error.response;
  }
};

// from / to: yyyy-MM-dd (inclusive); empty = last 30 days
export const getAiDraftModelStats = async (from, to) => {
  try {
    return await axiosInterceptor.get(`${adminUrl()}/model-stats`, { params: { from: from || undefined, to: to || undefined } });
  } catch (error) {
    return error.response;
  }
};

export const downloadAiDraftModelStatsCsv = async (from, to) => {
  try {
    return await axiosInterceptor.get(`${adminUrl()}/model-stats`, {
      params: { from: from || undefined, to: to || undefined, format: "csv" },
      responseType: "blob",
    });
  } catch (error) {
    return error.response;
  }
};

// ---- AI history summary (AiHistorySummaryController): every radiologist on AI draft sites ----

const historySummaryUrl = (studyId) => `${API_BASE_URL}${SERVICEURL}/study/${studyId}/ai-history-summary`;

// { enabled, hasHistory, noteCount, documentCount, summary: {status, running, summary_html, stale, sources, ...} }
export const getHistorySummary = async (studyId) => {
  try {
    return await axiosInterceptor.get(historySummaryUrl(studyId));
  } catch (error) {
    return error.response;
  }
};

// 202 with the summary view; 409 one is already running; 422 nothing to summarise
export const requestHistorySummary = async (studyId) => {
  try {
    return await axiosInterceptor.post(historySummaryUrl(studyId), {});
  } catch (error) {
    return error.response;
  }
};
